import { useCallback, useMemo, useState } from "react";
import i18n from "../../../i18n";

export default function useCarousel<T>(items: T[], defaultIndex: number = 0, wrapAround: boolean = true) {
	const [currentIndex, setCurrentIndex] = useState<number>(defaultIndex);
	const [displayVal, setDisplayVal] = useState<string | number>(currentIndex + 1);

	const activeIndex = items.length > 0 ? Math.max(0, Math.min(currentIndex, items.length - 1)) : 0;

	const updateStates = useCallback((computeNext: (prev: number) => number) => {
		setCurrentIndex((prev) => {
			const next = computeNext(prev);
			setDisplayVal(next + 1);
			return next;
		});
	}, []);

	const handleBack = useCallback(() => {
		if (items.length === 0) return;

		updateStates((prev) => (wrapAround ? (prev - 1 + items.length) % items.length : Math.max(0, prev - 1)));
	}, [items.length, wrapAround, updateStates]);

	const handleNext = useCallback(() => {
		if (items.length === 0) return;
		updateStates((prev) => (wrapAround ? (prev + 1) % items.length : Math.min(items.length - 1, prev + 1)));
	}, [items.length, wrapAround, updateStates]);

	const handleJump = useCallback(
		(index: number) => {
			if (items.length === 0) return;
			// We only update state if the index is actually within the valid range
			if (index >= 0 && index < items.length) {
				updateStates(() => index);
			}
		},
		[items.length, updateStates],
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setDisplayVal(val);
			const num = parseInt(val, 10);
			if (!isNaN(num)) {
				handleJump(num - 1);
			}
		},
		[handleJump],
	);

	const handleInputBlur = useCallback(() => {
		setDisplayVal(activeIndex + 1);
	}, [activeIndex]);

	const currentItem = useMemo(() => items[activeIndex], [items, activeIndex]);

	const atEnd = activeIndex === items.length - 1;
	const atStart = activeIndex === 0;

	const hasMultipleItems = items.length > 1;

	return {
		currentIndex: activeIndex,
		currentItem,
		hasMultipleItems,
		inputControl: {
			value: displayVal,
			onChange: handleInputChange,
			onBlur: handleInputBlur,
		},
		goToIndex: handleJump,
		backControl: {
			handle: handleBack,
			disabled: !wrapAround && atStart,
			ariaLabel: i18n.t("portal.components.useCarouselTSX.previous") as string,
		},
		nextControl: {
			handle: handleNext,
			disabled: !wrapAround && atEnd,
			ariaLabel: i18n.t("portal.components.useCarouselTSX.next") as string,
		},
	};
}

export type CarouselControl<T> = ReturnType<typeof useCarousel<T>>;
